import { TypedNode, ViewModel } from '@rehaje/core'
import {
  createForm as createNewForm,
  SchemaAnyModel,
  SchemaAnyNode,
  SchemaArrayModel,
  SchemaBooleanModel,
  SchemaBooleanNode,
  SchemaDateModel,
  SchemaDateNode,
  SchemaDateTimeModel,
  SchemaDateTimeNode,
  SchemaDynamicModel,
  SchemaDynamicNode,
  SchemaFunctionModel,
  SchemaFunctionNode,
  SchemaIntersectionModel,
  SchemaIntersectionNode,
  SchemaMapModel,
  SchemaMapNode,
  SchemaModel,
  SchemaNode,
  SchemaNullModel,
  SchemaNullNode,
  SchemaNumberModel,
  SchemaNumberNode,
  SchemaObjectModel,
  SchemaObjectNode,
  SchemaReferenceModel,
  SchemaReferenceNode,
  SchemaSetModel,
  SchemaSetNode,
  SchemaStringModel,
  SchemaStringNode,
  SchemaTypedSchemaModel,
  SchemaTypedSchemaNode,
  SchemaUndefinedModel,
  SchemaUndefinedNode,
  SchemaUnionModel,
  SchemaUnionNode,
  SchemaUnknownModel,
  SchemaUnknownNode,
  SchemaURLSearchParamsModel,
  SchemaURLSearchParamsNode,
  SchemaVoidModel,
  SchemaVoidNode,
  ValuableModel,
  ValuableNode
} from '@rehaje/form'
import { RecursionView as RecursionViewNew, useViewModel } from '@rehaje/react'
import { observer } from '@rehaje/reactive-react'
import { hoistStatic, toArr } from '@rehaje/shared'
import { createElement } from 'react'

import type {
  Store,
  TypedSpecific,
  TypedSpecificLike,
  ViewModelConstructor
} from '@rehaje/core'
import type { CreateFormProps } from '@rehaje/form'
import type { ComponentProps, ComponentType, FC, ReactElement } from 'react'

export type ITypedNode = TypedSpecificLike

/**
 * 视图模型基类，表单系统中所有模型的基础类型
 */
export type TypedModel = ViewModel

/**
 * 视图模型基类，提供模型生命周期和事件系统
 */
export const TypedModel = ViewModel

/**
 * 表单节点类型，表单系统中所有节点的基础类型
 */
export type FormNode = TypedNode

/**
 * 表单节点基类，提供节点的基本能力
 */
export const FormNode = TypedNode

/**
 * 可持有值的节点，表单字段的底层数据结构
 */
export type FormValueNode = ValuableNode

/**
 * 可持有值的节点，提供值存储的基础结构
 */
export const FormValueNode = ValuableNode

/**
 * 表单架构节点基类，表单结构的底层数据类型
 */
export type FormGenericNode = SchemaNode

/**
 * 表单架构节点基类，管理表单结构和状态
 */
export const FormGenericNode = SchemaNode

/**
 * 接受任意值类型的表单字段节点
 */
export type FormAnyNode = SchemaAnyNode

/**
 * 接受任意值类型的表单字段节点
 */
export const FormAnyNode = SchemaAnyNode

/**
 * 布尔值表单字段节点
 */
export type FormBooleanNode = SchemaBooleanNode

/**
 * 布尔值表单字段节点
 */
export const FormBooleanNode = SchemaBooleanNode

/**
 * 日期表单字段节点
 */
export type FormDateNode = SchemaDateNode

/**
 * 日期表单字段节点
 */
export const FormDateNode = SchemaDateNode

/**
 * 日期时间表单字段节点
 */
export type FormDateTimeNode = SchemaDateTimeNode

/**
 * 日期时间表单字段节点
 */
export const FormDateTimeNode = SchemaDateTimeNode

/**
 * 动态类型表单字段节点
 */
export type FormDynamicNode = SchemaDynamicNode

/**
 * 动态类型表单字段节点
 */
export const FormDynamicNode = SchemaDynamicNode

/**
 * 函数类型表单字段节点
 */
export type FormFunctionNode = SchemaFunctionNode

/**
 * 函数类型表单字段节点
 */
export const FormFunctionNode = SchemaFunctionNode

/**
 * 交叉类型表单字段节点
 */
export type FormIntersectionNode = SchemaIntersectionNode

/**
 * 交叉类型表单字段节点
 */
export const FormIntersectionNode = SchemaIntersectionNode

/**
 * Map类型表单字段节点
 */
export type FormMapNode = SchemaMapNode

/**
 * Map类型表单字段节点
 */
export const FormMapNode = SchemaMapNode

/**
 * null值表单字段节点
 */
export type FormNullNode = SchemaNullNode

/**
 * null值表单字段节点
 */
export const FormNullNode = SchemaNullNode

/**
 * 数字表单字段节点
 */
export type FormNumberNode = SchemaNumberNode

/**
 * 数字表单字段节点
 */
export const FormNumberNode = SchemaNumberNode

/**
 * 对象表单字段节点
 */
export type FormObjectNode = SchemaObjectNode

/**
 * 对象表单字段节点
 */
export const FormObjectNode = SchemaObjectNode

/**
 * 引用类型表单字段节点
 */
export type FormReferenceNode = SchemaReferenceNode

/**
 * 引用类型表单字段节点
 */
export const FormReferenceNode = SchemaReferenceNode

/**
 * Set集合表单字段节点
 */
export type FormSetNode = SchemaSetNode

/**
 * Set集合表单字段节点
 */
export const FormSetNode = SchemaSetNode

/**
 * 字符串表单字段节点
 */
export type FormStringNode = SchemaStringNode

/**
 * 字符串表单字段节点
 */
export const FormStringNode = SchemaStringNode

/**
 * 类型化架构表单字段节点
 */
export type FormTypedSchemaNode = SchemaTypedSchemaNode

/**
 * 类型化架构表单字段节点
 */
export const FormTypedSchemaNode = SchemaTypedSchemaNode

/**
 * URL搜索参数表单字段节点
 */
export type FormURLSearchParamsNode = SchemaURLSearchParamsNode

/**
 * URL搜索参数表单字段节点
 */
export const FormURLSearchParamsNode = SchemaURLSearchParamsNode

/**
 * undefined值表单字段节点
 */
export type FormUndefinedNode = SchemaUndefinedNode

/**
 * undefined值表单字段节点
 */
export const FormUndefinedNode = SchemaUndefinedNode

/**
 * 联合类型表单字段节点
 */
export type FormUnionNode = SchemaUnionNode

/**
 * 联合类型表单字段节点
 */
export const FormUnionNode = SchemaUnionNode

/**
 * 未知类型表单字段节点
 */
export type FormUnknownNode = SchemaUnknownNode

/**
 * 未知类型表单字段节点
 */
export const FormUnknownNode = SchemaUnknownNode

/**
 * void类型表单字段节点
 */
export type FormVoidNode = SchemaVoidNode

/**
 * void类型表单字段节点
 */
export const FormVoidNode = SchemaVoidNode

/**
 * 可持有值的模型，继承自 SchemaModel，具备表单字段的基本能力
 */
export type FormValueModel = ValuableModel

/**
 * 可持有值的模型，提供值管理、校验和状态处理能力
 */
export const FormValueModel = ValuableModel

/**
 * 表单架构模型基类，提供表单结构和行为的基础定义
 */
export type FormGenericModel = SchemaModel

/**
 * 表单架构模型基类，管理表单状态、错误和校验信息
 */
export const FormGenericModel = SchemaModel

/**
 * 接受任意值类型的表单字段模型
 */
export type FormAnyModel = SchemaAnyModel

/**
 * 接受任意值类型的表单字段模型
 */
export const FormAnyModel = SchemaAnyModel

/**
 * 布尔值表单字段模型，用于管理 true/false 值
 */
export type FormBooleanModel = SchemaBooleanModel

/**
 * 布尔值表单字段模型，用于管理 true/false 值
 */
export const FormBooleanModel = SchemaBooleanModel

/**
 * 日期表单字段模型，管理日期数据（不含时间部分）
 */
export type FormDateModel = SchemaDateModel

/**
 * 日期表单字段模型，管理日期数据（不含时间部分）
 */
export const FormDateModel = SchemaDateModel

/**
 * 日期时间表单字段模型，管理包含日期和时间的数据
 */
export type FormDateTimeModel = SchemaDateTimeModel

/**
 * 日期时间表单字段模型，管理包含日期和时间的数据
 */
export const FormDateTimeModel = SchemaDateTimeModel

/**
 * 动态类型表单字段模型，可在运行时改变其类型定义
 */
export type FormDynamicModel = SchemaDynamicModel

/**
 * 动态类型表单字段模型，可在运行时改变其类型定义
 */
export const FormDynamicModel = SchemaDynamicModel

/**
 * 函数类型表单字段模型，支持参数定义和返回值类型
 */
export type FormFunctionModel = SchemaFunctionModel

/**
 * 函数类型表单字段模型，支持参数定义和返回值类型
 */
export const FormFunctionModel = SchemaFunctionModel

/**
 * 交叉类型表单字段模型，组合多个模型的特性
 */
export type FormIntersectionModel = SchemaIntersectionModel

/**
 * 交叉类型表单字段模型，组合多个模型的特性
 */
export const FormIntersectionModel = SchemaIntersectionModel

/**
 * Map类型表单字段模型，管理键值对集合
 */
export type FormMapModel = SchemaMapModel

/**
 * Map类型表单字段模型，管理键值对集合
 */
export const FormMapModel = SchemaMapModel

/**
 * null值表单字段模型
 */
export type FormNullModel = SchemaNullModel

/**
 * null值表单字段模型
 */
export const FormNullModel = SchemaNullModel

/**
 * 数字表单字段模型，支持最大值和最小值限制
 */
export type FormNumberModel = SchemaNumberModel

/**
 * 数字表单字段模型，支持最大值和最小值限制
 */
export const FormNumberModel = SchemaNumberModel

/**
 * 对象表单字段模型，管理具有属性结构的复杂数据
 * 支持属性定义、额外属性和属性数量限制
 */
export type FormObjectModel = SchemaObjectModel

/**
 * 对象表单字段模型，管理具有属性结构的复杂数据
 * 支持属性定义、额外属性和属性数量限制
 */
export const FormObjectModel = SchemaObjectModel

/**
 * 引用类型表单字段模型，引用其他模型定义
 */
export type FormReferenceModel = SchemaReferenceModel

/**
 * 引用类型表单字段模型，引用其他模型定义
 */
export const FormReferenceModel = SchemaReferenceModel

/**
 * Set集合表单字段模型，管理唯一值集合
 */
export type FormSetModel = SchemaSetModel

/**
 * Set集合表单字段模型，管理唯一值集合
 */
export const FormSetModel = SchemaSetModel

/**
 * 字符串表单字段模型，支持模式匹配、格式和长度限制
 */
export type FormStringModel = SchemaStringModel

/**
 * 字符串表单字段模型，支持模式匹配、格式和长度限制
 */
export const FormStringModel = SchemaStringModel

/**
 * 类型化架构表单字段模型，用于复杂类型定义
 */
export type FormTypedSchemaModel = SchemaTypedSchemaModel

/**
 * 类型化架构表单字段模型，用于复杂类型定义
 */
export const FormTypedSchemaModel = SchemaTypedSchemaModel

/**
 * URL搜索参数表单字段模型，处理URL查询字符串
 */
export type FormURLSearchParamsModel = SchemaURLSearchParamsModel

/**
 * URL搜索参数表单字段模型，处理URL查询字符串
 */
export const FormURLSearchParamsModel = SchemaURLSearchParamsModel

/**
 * undefined值表单字段模型
 */
export type FormUndefinedModel = SchemaUndefinedModel

/**
 * undefined值表单字段模型
 */
export const FormUndefinedModel = SchemaUndefinedModel

/**
 * 联合类型表单字段模型，支持多种可能类型
 */
export type FormUnionModel = SchemaUnionModel

/**
 * 联合类型表单字段模型，支持多种可能类型
 */
export const FormUnionModel = SchemaUnionModel

/**
 * 未知类型表单字段模型，处理类型未明确的数据
 */
export type FormUnknownModel = SchemaUnknownModel

/**
 * 未知类型表单字段模型，处理类型未明确的数据
 */
export const FormUnknownModel = SchemaUnknownModel

/**
 * void类型表单字段模型
 */
export type FormVoidModel = SchemaVoidModel

/**
 * void类型表单字段模型
 */
export const FormVoidModel = SchemaVoidModel

/**
 * 数组类型表单字段模型，管理数组数据
 */
export type FormArrayModel = SchemaArrayModel

/**
 * 数组类型表单字段模型，管理数组数据
 */
export const FormArrayModel = SchemaArrayModel

/**
 * 获取指定类型的视图模型实例的钩子函数
 * 会对模型类型进行检查，确保类型匹配
 *
 * @param factory - 视图模型构造函数
 * @returns 当前上下文中的视图模型实例，若不存在或类型不匹配则返回undefined
 */
export const useTypedModel = <T extends ViewModel>(
  factory?: ViewModelConstructor<T>
): T | undefined => {
  const model = useViewModel()

  if (factory && model && !(model instanceof factory)) {
    throw new Error(`${factory.name} is not a constructor of ${model}`)
  }

  if (!model) return

  return new Proxy(model, {
    get(target, prop, receiver) {
      if (prop === 'type') return model?.__r_node
      return Reflect.get(target, prop, receiver)
    }
  }) as T
}

/**
 * 创建表单实例
 * 添加中间件支持Form前缀到Schema前缀的转换
 *
 * @param specific - 类型指定器，定义表单结构
 * @param props - 创建表单的配置选项
 * @returns 创建的表单实例
 */
export const createForm = <T extends TypedSpecific>(
  specific: T,
  props: CreateFormProps = {}
): Store<T> => {
  return createNewForm(specific, {
    ...props,
    plugins: [
      {
        middlewares: {
          createTypedNode(ctx, next) {
            const payload = ctx.payload
            const type = payload?.type
            if (
              typeof payload === 'string' &&
              payload.startsWith('{{') &&
              ctx.node?.type !== 'Mustache' &&
              ctx.node?.type !== 'Expression' &&
              ctx.node?.type !== 'JSExpression'
            ) {
              ctx.payload = {
                code: payload,
                type: 'Mustache'
              }
              return next(ctx)
            }
            if (typeof payload !== 'object' || payload == null) return next(ctx)
            if (
              typeof type === 'string' &&
              type.startsWith('Form') &&
              type !== 'Form'
            ) {
              payload.type = payload.type.replace('Form', 'Schema.')
            }
            return next(ctx)
          }
        }
      },
      ...(props?.plugins ?? [])
    ]
  })
}

/**
 * 组件映射器接口，将一个组件转换为另一个组件
 */
export interface IComponentMapper<T extends ComponentType> {
  (target: T): ComponentType
}

/**
 * 工厂接口，定义可创建特定类型实例的函数
 */
export type IFactory<T> = Function & {
  prototype: T
}

/**
 * 属性映射器类型，将组件属性与模型数据结合
 * 用于在connect高阶函数中转换组件属性
 */
export type PropsMapper<T extends ComponentType, F extends TypedModel> = (
  props: ComponentProps<T>,
  model: F
) => Partial<ComponentProps<T>>

/**
 * 视图映射器类型，使用模型数据转换组件的渲染函数
 */
export type ViewMapper<T extends ComponentType, F extends TypedModel> = (
  props: ComponentProps<T>,
  model: F
) => (prev: () => ReactElement) => ReactElement

/**
 * 连接React组件与表单模型的高阶函数
 * 实现数据驱动的表单组件
 *
 * @param target - 目标React组件
 * @param mapModel - 模型构造函数
 * @param mapProps - 属性映射器，转换组件属性
 * @param mapView - 视图映射器，自定义渲染逻辑
 * @returns 连接了模型的新组件
 */
export function connect<
  T extends ComponentType<any>,
  F extends ViewModelConstructor = ViewModelConstructor
>(
  target: T,
  mapModel: F,
  mapProps?:
    | PropsMapper<T, InstanceType<F>>
    | PropsMapper<T, InstanceType<F>>[],
  mapView?: ViewMapper<T, InstanceType<F>> | ViewMapper<T, InstanceType<F>>[]
): T {
  if (!target) return target
  const _mapProps = toArr(mapProps) as PropsMapper<T, InstanceType<F>>[]
  const _mapView = toArr(mapView) as ViewMapper<T, InstanceType<F>>[]
  const WrapComponent = observer(
    (props: ComponentProps<T>) => {
      const model = useTypedModel(mapModel) as InstanceType<F>
      const mergedProps = _mapProps.reduce((buf, mapper) => {
        if (typeof mapper === 'function') {
          const result = mapper(buf, model)
          if (result === buf) return buf
          return { ...buf, ...mapper(buf, model) }
        }
        return buf
      }, props)
      const render = _mapView.reduce(
        (prev, view) => {
          if (typeof view === 'function')
            return () => view(mergedProps, model)(prev)
          return prev
        },
        () => createElement(target, mergedProps)
      )
      return render()
    },
    {
      forwardRef: true
    }
  )

  hoistStatic(WrapComponent, target)

  return WrapComponent as any
}

export {
  createStore,
  skipCreateTypedNode as markParsed,
  Store,
  TypedNode
} from '@rehaje/core'
export const RecursionView: FC<{ node?: TypedNode; type?: TypedNode }> = (
  props
) => {
  if (!props.node && !props.type) return null
  return createElement(RecursionViewNew, {
    ...props,
    node: props.node ?? props.type!
  })
}
/**
 * 所有表单模型类型的联合类型
 */
export type FormModelUnion =
  | FormAnyModel
  | FormBooleanModel
  | FormDateModel
  | FormDateTimeModel
  | FormDynamicModel
  | FormFunctionModel
  | FormIntersectionModel
  | FormMapModel
  | FormNullModel
  | FormNumberModel
  | FormObjectModel
  | FormReferenceModel
  | FormSetModel
  | FormStringModel
  | FormTypedSchemaModel
  | FormUndefinedModel
  | FormUnionModel
  | FormUnknownModel
  | FormURLSearchParamsModel
  | FormVoidModel
/**
 * 所有表单节点类型的联合类型
 */
export type FormNodeUnion =
  | FormAnyNode
  | FormBooleanNode
  | FormDateNode
  | FormDateTimeNode
  | FormDynamicNode
  | FormFunctionNode
  | FormIntersectionNode
  | FormMapNode
  | FormNullNode
  | FormNumberNode
  | FormObjectNode
  | FormReferenceNode
  | FormSetNode
  | FormStringNode
  | FormTypedSchemaNode
  | FormUndefinedNode
  | FormUnionNode
  | FormUnknownNode
  | FormURLSearchParamsNode
  | FormVoidNode
export { Form, type SchemaText } from '@rehaje/form'
export { useStore, useTypedNode, View } from '@rehaje/react'
export * from '@rehaje/reactive'
export { observer } from '@rehaje/reactive-react'
export { Path } from '@rehaje/shared'
